import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Toast } from '../components/Toast';
import { ReportSettingsDrawer } from '../components/MonthlyClubReport/ReportSettingsDrawer';
import { StatCard } from '../components/StatCard';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { ScheduleReportsModal } from '../components/ScheduleReportsModal';
import { SearchBar } from '../components/SearchBar';
import { FilterChip } from '../components/FilterChip';
import { DateRangePicker } from '../components/DateRangePicker';
import { Plus, Download, Eye, MoreVertical, Calendar, FileText, Edit, Pause, Play, Trash2, RefreshCw, AlertCircle, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
// Mock data for scheduled reports
const MONTHLY_CLUB_REPORT_ID = 'monthly-club-report';
const MONTHLY_CLUB_REPORT_PATH = '/operations/reports/monthly-club-report';
const mockScheduledReports = [{
  id: MONTHLY_CLUB_REPORT_ID,
  name: 'Monthly Club Report',
  frequency: 'Monthly',
  nextRun: '1 Nov 2026 at 7:00 AM',
  recipients: ['owner@hillsideestate.com.au', 'clubmanager@hillsideestate.com.au'],
  status: 'Active' as const
}, {
  id: '2',
  name: 'Weekly Revenue Summary',
  frequency: 'Weekly',
  nextRun: 'Jan 10, 2026 at 8:00 AM',
  recipients: ['finance@winery.com'],
  status: 'Active' as const
}, {
  id: '3',
  name: 'Quarterly Churn Analysis',
  frequency: 'Quarterly',
  nextRun: 'Mar 1, 2026 at 10:00 AM',
  recipients: ['team@winery.com', 'analytics@winery.com', 'ops@winery.com'],
  status: 'Paused' as const
}];
// Mock data for report history
// clubReportMonth links a row to that month of the Monthly Club Report
const mockReportHistory: {
  id: string;
  name: string;
  generated: string;
  generatedBy: string;
  fileSize: string;
  status: 'Completed' | 'Failed' | 'Processing';
  error?: string;
  clubReportMonth?: string;
}[] = [{
  id: 'club-2026-09',
  name: 'Monthly Club Report · September 2026',
  generated: '1 Oct 2026 at 7:00 AM',
  generatedBy: 'Scheduled',
  fileSize: '1.1 MB',
  status: 'Completed',
  clubReportMonth: '2026-09'
}, {
  id: 'club-2026-08',
  name: 'Monthly Club Report · August 2026',
  generated: '1 Sep 2026 at 7:00 AM',
  generatedBy: 'Scheduled',
  fileSize: '1.1 MB',
  status: 'Completed',
  clubReportMonth: '2026-08'
}, {
  id: 'club-2026-07',
  name: 'Monthly Club Report · July 2026',
  generated: '1 Aug 2026 at 7:00 AM',
  generatedBy: 'Scheduled',
  fileSize: '1.0 MB',
  status: 'Completed',
  clubReportMonth: '2026-07'
}, {
  id: '2',
  name: 'Weekly Revenue Summary',
  generated: 'Jan 10, 2026 at 8:00 AM',
  generatedBy: 'Scheduled',
  fileSize: '1.8 MB',
  status: 'Completed' as const
}, {
  id: '3',
  name: 'Custom Member Export',
  generated: 'Jan 8, 2026 at 3:45 PM',
  generatedBy: 'Chris Admin',
  fileSize: '5.1 MB',
  status: 'Completed' as const
}, {
  id: '4',
  name: 'Shipment Report',
  generated: 'Jan 7, 2026 at 11:20 AM',
  generatedBy: 'Sarah Admin',
  fileSize: '3.2 MB',
  status: 'Failed' as const,
  error: 'Database connection timeout'
}, {
  id: '5',
  name: 'Revenue Report',
  generated: 'Jan 6, 2026 at 9:15 AM',
  generatedBy: 'Scheduled',
  fileSize: '—',
  status: 'Processing' as const
}];
export function ReportsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'scheduled' | 'history'>('scheduled');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isClubReportSettingsOpen, setIsClubReportSettingsOpen] = useState(false);
  const [showSettingsSavedToast, setShowSettingsSavedToast] = useState(false);
  const openClubReport = (month?: string) => navigate(month ? `${MONTHLY_CLUB_REPORT_PATH}?month=${month}` : MONTHLY_CLUB_REPORT_PATH);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState<string[]>([]);
  const [dateRangeFilter, setDateRangeFilter] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: '',
    endDate: ''
  });
  // Filter report history
  const filteredReportHistory = useMemo(() => {
    return mockReportHistory.filter(report => {
      // Search filter
      if (searchQuery && !report.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Report type filter
      if (reportTypeFilter.length > 0) {
        const matchesType = reportTypeFilter.some(type => {
          if (type === 'all') return true;
          return report.name.toLowerCase().includes(type.toLowerCase());
        });
        if (!matchesType) return false;
      }
      return true;
    });
  }, [searchQuery, reportTypeFilter]);
  const handleScheduleReport = (schedule: any) => {
    console.log('Schedule report:', schedule);
    setIsScheduleModalOpen(false);
  };
  const handleDownload = (reportId: string) => {
    console.log('Download report:', reportId);
  };
  const handleRetry = (reportId: string) => {
    console.log('Retry report:', reportId);
  };
  const handleEditSchedule = (reportId: string) => {
    if (reportId === MONTHLY_CLUB_REPORT_ID) {
      setIsClubReportSettingsOpen(true);
      return;
    }
    setIsScheduleModalOpen(true);
  };
  const handleTogglePause = (reportId: string) => {
    console.log('Toggle pause:', reportId);
  };
  const handleDeleteSchedule = (reportId: string) => {
    console.log('Delete schedule:', reportId);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-12743" data-mp="icrbmd6q8zym">
      {/* NavBar */}
      <div className="mb-6" data-id="element-12744" data-mp="ifltbwycw6oc">
        <NavBar tabs={[]} title="Reports" subtitle="Schedule, generate, and download reports" data-id="element-12745" data-mp="ncm3aiybttkq" />
      </div>

      {/* Header with Button */}
      <div className="flex items-center justify-between mb-8" data-id="element-12746" data-mp="1ts68zi2ultu9">
        <div data-id="element-12747" data-mp="1yqpvbjlct7n5"></div>
        <Button variant="primary" size="md" onClick={() => setIsScheduleModalOpen(true)} iconLeft={<Plus className="w-4 h-4" data-id="element-12749" data-mp="3utm56u1ebufn" />} data-id="element-12748" data-mp="1yq7ojt12fb0z">
          Schedule New Report
        </Button>
      </div>

      {/* Monthly Club Report */}
      <div className="mb-6" data-id="element-12750" data-mp="3qv500g1jcu0v">
        <Card data-id="element-12751" data-mp="8n76yttckf2a">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-12752" data-mp="8nyzfvrndwoc">
            <div className="flex items-start gap-3 min-w-0" data-id="element-12753" data-mp="3numugtv1m3e">
              <div className="p-2 bg-[#E0F2FE] rounded-lg flex-shrink-0" data-id="element-12754" data-mp="3m9t2tfde76l">
                <FileText className="w-5 h-5 text-[#0EA5E9]" data-id="element-12755" data-mp="3c4pbar9wv3xy" />
              </div>
              <div className="min-w-0" data-id="element-12756" data-mp="3764ez2xs54es">
                <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-12757" data-mp="3738dj4mq3xll">
                  Monthly Club Report
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-12758" data-mp="3h2qtzb3fd18w">
                  September 2026 report is ready · Generated 1 Oct 2026, 7:00
                  AM · Next report 1 Nov 2026
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-12759" data-mp="2oe20ny8m207p">
              <Button variant="outline" size="md" onClick={() => setIsClubReportSettingsOpen(true)} data-id="element-12760" data-mp="13waud7xws8d6">
                Report settings
              </Button>
              <Button variant="primary" size="md" onClick={() => openClubReport('2026-09')} data-id="element-12761" data-mp="18p3ypupgteoq">
                View current report
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-12762" data-mp="1mmsiez7sqfs7">
        <StatCard label="Reports Generated" value="24" secondaryValue="This month" data-id="element-12763" data-mp="1rm51d8vjr2pi" />
        <StatCard label="Scheduled Reports" value="6" secondaryValue="Active" data-id="element-12764" data-mp="1rjralc7wgcnr" />
        <StatCard label="Storage Used" value="1.2 GB" secondaryValue="of 5 GB" data-id="element-12765" data-mp="1dm2pq4du15ip">
          <div className="w-full bg-[#E2E8F0] rounded-full h-2 mt-2" data-id="element-12766" data-mp="1ik45ogfyoopy">
            <div className="bg-[#0EA5E9] h-2 rounded-full" style={{
            width: '24%'
          }} data-id="element-12767" data-mp="26nsczhpobn2" />
          </div>
        </StatCard>
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-12768" data-mp="74767yahymeh" />

      {/* Tab Navigation */}
      <div className="border-b border-[#E2E8F0] mb-6" data-id="element-12769" data-mp="xx543sbcqmdw">
        <div className="flex gap-0" data-id="element-12770" data-mp="tv07qltirbnk">
          <button onClick={() => setActiveTab('scheduled')} className={`h-[44px] px-4 text-[14px] font-medium transition-colors duration-200 ${activeTab === 'scheduled' ? 'text-[#0EA5E9] border-b-2 border-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-12771" data-mp="ttyzep3w139j">
            Scheduled Reports
          </button>
          <button onClick={() => setActiveTab('history')} className={`h-[44px] px-4 text-[14px] font-medium transition-colors duration-200 ${activeTab === 'history' ? 'text-[#0EA5E9] border-b-2 border-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-12772" data-mp="1hjtb33ch0ls7">
            Report History
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'scheduled' /* Scheduled Reports Tab */ ? <div data-id="element-12773" data-mp="20c5bhkjkdczp">
          {mockScheduledReports.length > 0 ? <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-12774" data-mp="1vjlvkgxem5rq">
              <div className="overflow-x-auto" data-id="element-12775" data-mp="22wk046ajld20">
                <table className="w-full" data-id="element-12776" data-mp="27ud9f8756n6i">
                  <thead className="bg-[#F1F5F9]" data-id="element-12777" data-mp="27v4qzf3wmkds">
                    <tr data-id="element-12778" data-mp="2ctg3jxll38zp">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12779" data-mp="2ct6lqxap0853">
                        Report Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12780" data-mp="2lue5898jv4f4">
                        Frequency
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12781" data-mp="2vjxrlovh1geq">
                        Next Run
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12782" data-mp="30l3l3owyh7pb">
                        Recipients
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12783" data-mp="39k8haol1ooyb">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12784" data-mp="q56fcxd7044h">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]" data-id="element-12785" data-mp="zv976u8kvc18">
                    {mockScheduledReports.map(report => <tr key={report.id} className="hover:bg-[#F8FAFC] transition-colors" style={{
                height: '52px'
              }} data-id="element-12786" data-mp="14sreyhauzl7n">
                        <td className="px-4 py-3" data-id="element-12787" data-mp="1dt7ghl81ki9v">
                          <button onClick={() => report.id === MONTHLY_CLUB_REPORT_ID ? openClubReport() : handleEditSchedule(report.id)} className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-12788" data-mp="1drmni01dl7e4">
                            {report.name}
                          </button>
                        </td>
                        <td className="px-4 py-3" data-id="element-12789" data-mp="3d0e9ngf5lvet">
                          <span className="text-[14px] text-[#475569]" data-id="element-12790" data-mp="3d1z1uzdzx29i">
                            {report.frequency}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-id="element-12791" data-mp="33bmkfg1h9p7l">
                          <span className="text-[14px] text-[#475569]" data-id="element-12792" data-mp="2ybrupftfv5o4">
                            {report.nextRun}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-id="element-12793" data-mp="3vxfp5v6c3dx0">
                          {report.recipients.length === 1 ? <span className="text-[14px] text-[#475569]" data-id="element-12794" data-mp="3r174615ggqhx">
                              {report.recipients[0]}
                            </span> : <span className="text-[14px] text-[#475569] cursor-help" title={report.recipients.join(', ')} data-id="element-12795" data-mp="3h29ir73btd35">
                              {report.recipients.length} recipients
                            </span>}
                        </td>
                        <td className="px-4 py-3" data-id="element-12796" data-mp="25lj7vanyjx37">
                          <Badge variant={report.status === 'Active' ? 'success' : 'default'} data-id="element-12797" data-mp="xnwzowaybxan">
                            {report.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3" data-id="element-12798" data-mp="npptn8i7aznf">
                          <div className="flex items-center gap-2" data-id="element-12799" data-mp="noy2qazjtq3h">
                            <button onClick={() => handleEditSchedule(report.id)} className="p-1.5 text-[#64748B] hover:text-[#0EA5E9] hover:bg-[#F1F5F9] rounded transition-colors" title="Edit" data-id="element-12800" data-mp="snrbjq3w4sxb">
                              <Edit className="w-4 h-4" data-id="element-12801" data-mp="iqmn1jmkoldc" />
                            </button>
                            <button onClick={() => handleTogglePause(report.id)} className="p-1.5 text-[#64748B] hover:text-[#0EA5E9] hover:bg-[#F1F5F9] rounded transition-colors" title={report.status === 'Active' ? 'Pause' : 'Resume'} data-id="element-12802" data-mp="4zrc81siju74">
                              {report.status === 'Active' ? <Pause className="w-4 h-4" data-id="element-12803" data-mp="3w59lc3384a0d" /> : <Play className="w-4 h-4" data-id="element-12804" data-mp="4ylr8rwbxs" />}
                            </button>
                            <button onClick={() => handleDeleteSchedule(report.id)} className="p-1.5 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" title="Delete" data-id="element-12805" data-mp="3ciy176qtdeb5">
                              <Trash2 className="w-4 h-4" data-id="element-12806" data-mp="3mi6r9oik68s8" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div> /* Empty State for Scheduled Reports */ : <div className="bg-white rounded-lg border border-[#E2E8F0] p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-12807" data-mp="3rh9x3b6d4j19">
              <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-12808" data-mp="91ftr0eye9b9">
                <Calendar className="w-8 h-8 text-[#94A3B8]" data-id="element-12809" data-mp="44fvvcs9ccv8" />
              </div>
              <h3 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-12810" data-mp="j1q6kjx2tepw">
                No scheduled reports yet
              </h3>
              <p className="text-[14px] text-[#64748B] mb-6 max-w-md mx-auto" data-id="element-12811" data-mp="e00tyzu7gszp">
                Set up automated reports to be delivered to your inbox
              </p>
              <Button variant="primary" onClick={() => setIsScheduleModalOpen(true)} data-id="element-12812" data-mp="2ci2v149c8jko">
                Schedule Your First Report
              </Button>
            </div>}
        </div> /* Report History Tab */ : <div data-id="element-12813" data-mp="2m8xksilsm1av">
          {/* Filter Bar */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-12814" data-mp="1olq8i87lf9y3">
            <div className="flex flex-col md:flex-row gap-4" data-id="element-12815" data-mp="1tk01cc5uhygj">
              <div className="flex-1" data-id="element-12816" data-mp="1tmmj14mg9uch">
                <SearchBar placeholder="Search reports..." value={searchQuery} onChange={setSearchQuery} data-id="element-12817" data-mp="1yjmobdbh6t2j" />
              </div>
              <div className="flex gap-3" data-id="element-12818" data-mp="23kk7nt881t5m">
                <FilterChip filter={{
              id: 'reportType',
              label: 'Report Type',
              options: [{
                id: 'all',
                label: 'All Types',
                value: 'all'
              }, {
                id: 'subscription',
                label: 'Subscription Report',
                value: 'subscription'
              }, {
                id: 'revenue',
                label: 'Revenue Report',
                value: 'revenue'
              }, {
                id: 'churn',
                label: 'Churn Report',
                value: 'churn'
              }, {
                id: 'member',
                label: 'Member Report',
                value: 'member'
              }, {
                id: 'shipment',
                label: 'Shipment Report',
                value: 'shipment'
              }],
              selectedValue: reportTypeFilter[0] || 'all'
            }} onSelect={value => setReportTypeFilter([value])} data-id="element-12819" data-mp="39z1jcbk4jav0" />
              </div>
            </div>
          </div>

          {filteredReportHistory.length > 0 ? <>
              <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-12820" data-mp="2011syesd4uqd">
                <div className="overflow-x-auto" data-id="element-12821" data-mp="3ug8nckv55l0b">
                  <table className="w-full" data-id="element-12822" data-mp="3uffhdm40azj8">
                    <thead className="bg-[#F1F5F9]" data-id="element-12823" data-mp="33nqo5gkeajf">
                      <tr data-id="element-12824" data-mp="3kj40sjxdvniz">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12825" data-mp="3kjvkoxhmbwtf">
                          Report Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12826" data-mp="h1u7e8tdttvv">
                          Generated
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12827" data-mp="74g317kqgvjx">
                          Generated By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12828" data-mp="2ruev07lduu0w">
                          File Size
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12829" data-mp="2wr5b2ux6tpmb">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12830" data-mp="19wjzz609yl5m">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-12831" data-mp="w55comzc3xsr">
                      {filteredReportHistory.map(report => <tr key={report.id} className="hover:bg-[#F8FAFC] transition-colors" style={{
                  height: '52px'
                }} data-id="element-12832" data-mp="r6u40fkrztc4">
                          <td className="px-4 py-3" data-id="element-12833" data-mp="14z0bzedryu0h">
                            <span className="text-[14px] text-[#0F172A]" data-id="element-12834" data-mp="106ql9w4u5px5">
                              {report.name}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-12835" data-mp="8g2qd1tr9grx">
                            <span className="text-[14px] text-[#475569]" data-id="element-12836" data-mp="8fkif7xqde46">
                              {report.generated}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-12837" data-mp="12a86pn7c4yuf">
                            <div className="flex items-center gap-2" data-id="element-12838" data-mp="16bjmz24bk7py">
                              {report.generatedBy === 'Scheduled' && <Calendar className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-12839" data-mp="1bawcxvqhluwr" />}
                              <span className="text-[14px] text-[#475569]" data-id="element-12840" data-mp="1g9pohim7rbua">
                                {report.generatedBy}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-12841" data-mp="1q0bymxb5va5l">
                            <span className="text-[14px] text-[#475569]" data-id="element-12842" data-mp="1q1n3z1k98pyt">
                              {report.fileSize}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-12843" data-mp="1yxuoqg3nweoc">
                            <div className="flex items-center gap-2" data-id="element-12844" data-mp="23y8ufrhsjcj7">
                              {report.status === 'Completed' && <Badge variant="success" data-id="element-12845" data-mp="2e40daigw5xd2">
                                  <CheckCircle className="w-3 h-3 mr-1 inline" data-id="element-12846" data-mp="293vyqxzjncph" />
                                  Completed
                                </Badge>}
                              {report.status === 'Failed' && <div className="flex items-center gap-2" data-id="element-12847" data-mp="2037hzvirmy8w">
                                  <Badge variant="error" data-id="element-12848" data-mp="2050pte547oih">
                                    <XCircle className="w-3 h-3 mr-1 inline" data-id="element-12849" data-mp="2wtq998we4p6k" />
                                    Failed
                                  </Badge>
                                  {report.error && <span className="cursor-help" title={report.error} data-id="element-12850" data-mp="2n3f5pngmci07">
                                      <AlertCircle className="w-4 h-4 text-[#EF4444]" data-id="element-12851" data-mp="2j21x55m560pl" />
                                    </span>}
                                </div>}
                              {report.status === 'Processing' && <Badge variant="warning" data-id="element-12852" data-mp="3at9d3pi65ed2">
                                  <Loader2 className="w-3 h-3 mr-1 inline animate-spin" data-id="element-12853" data-mp="3aroi5yrxzteo" />
                                  Processing
                                </Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-12854" data-mp="w7zuj84lnrvo">
                            {report.status === 'Completed' && <div className="flex items-center gap-2" data-id="element-12855" data-mp="w9ueaj44xdy6">
                                {report.clubReportMonth && <Button variant="outline" size="sm" onClick={() => openClubReport(report.clubReportMonth)} iconLeft={<Eye className="w-3.5 h-3.5" data-id="element-12857" data-mp="1oyhtbwyaj73a" />} data-id="element-12856" data-mp="rb9fmo4j72bu">
                                    View
                                  </Button>}
                                <Button variant="outline" size="sm" onClick={() => handleDownload(report.id)} iconLeft={<Download className="w-3.5 h-3.5" data-id="element-12859" data-mp="1a8kc60ys3hnl" />} data-id="element-12858" data-mp="1f5u1lgm63tbf">
                                  Download
                                </Button>
                              </div>}
                            {report.status === 'Failed' && <button onClick={() => handleRetry(report.id)} className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-12860" data-mp="22voc3rj0l5si">
                                Retry
                              </button>}
                            {report.status === 'Processing' && <span className="text-[14px] text-[#94A3B8]" data-id="element-12861" data-mp="2k5ip5mm1wz7c">
                                —
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Download Expiry Notice */}
              <div className="text-[12px] text-[#94A3B8] text-center" data-id="element-12862" data-mp="2y3gs7vux997j">
                Download links expire 30 days after generation
              </div>
            </> /* Empty State for Report History */ : <div className="bg-white rounded-lg border border-[#E2E8F0] p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-12863" data-mp="37ul4gnt7qfyo">
              <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-12864" data-mp="3gw190excjnuz">
                <FileText className="w-8 h-8 text-[#94A3B8]" data-id="element-12865" data-mp="1hqrpvfcc88v1" />
              </div>
              <h3 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-12866" data-mp="1cs6pnfx35p6l">
                No reports generated yet
              </h3>
              <p className="text-[14px] text-[#64748B] mb-6 max-w-md mx-auto" data-id="element-12867" data-mp="1qkmsogpzln7i">
                Generate a report manually or set up a scheduled report
              </p>
              <Button variant="primary" onClick={() => setIsScheduleModalOpen(true)} data-id="element-12868" data-mp="20hivc5t7ff45">
                Generate Report
              </Button>
            </div>}
        </div>}

      {/* Schedule Reports Modal */}
      <ScheduleReportsModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} onSave={handleScheduleReport} data-id="element-12869" data-mp="29iz5ccg3xf7u" />

      <ReportSettingsDrawer isOpen={isClubReportSettingsOpen} onClose={() => setIsClubReportSettingsOpen(false)} onSaved={() => setShowSettingsSavedToast(true)} data-id="element-12870" data-mp="25hdka3tf5umk" />
      {showSettingsSavedToast && <Toast id="report-settings-saved" type="success" title="Report settings saved" onClose={() => setShowSettingsSavedToast(false)} data-id="element-12871" data-mp="b45wux7rwlay" />}
    </div>;
}